/**
 * **고객 가져오기 화면의 판정·조립 한 곳** — 의존성 없는 순수 모듈이다(Vue·Element·`@/` 별칭 금지 — 단위 시험이 node 에서
 * 그대로 읽는다). I/O·시계·`$t` 가 없고 던지지 않는다 — 시각은 인자로 받고, 요청·타이머·`FormData`·`$router` 는 화면
 * (`views/CustomerImportView.vue`, 경계)이 한다. 문장은 [customerImportText.js](./customerImportText.js) 에만 있다.
 *
 * **판정·계산은 전부 서버(appserver WP-AP4)다.** 칸 맞추기·정리·미리보기 계산(시안의 `planImport`·`importBlock`)을 여기 옮기지
 * 않는다 — 화면은 서버가 준 작업 보기(`ImportView`, 계약 I4)를 그리는 법만 안다. 가져오기를 누를 때 남은 일도 서버의
 * `preview.block` 이 답한다([blockNoticeOf] 는 그 어휘를 문장과 자리로 바꿀 뿐이다).
 *
 * 계약 정본: `ordern-workspace/docs/planning/pos2lite-phone-order-impl-20261002/wp-ap4-customer-import-brief.md` §1-0 I1~I11
 * (준비 문서 §3-1 이 덮은 것 — I9 는 앱 목록 규약 + `caller`, I7 포인트 다시 시도는 본문 `{}`) · 화면 브리프 `wp-w1-admin-web-brief.md`.
 */
// 확장자를 적는 이유: 단위 시험이 앱 번들 밖(node)에서 이 파일을 그대로 읽는다 — node ESM 은 확장자를 추론하지 않는다
import { NETWORK_ERROR_MESSAGE, rejectionFieldsOf } from '../plugins/http/policy.js'
import { ADMIN_ROLE } from './adminRole.js'
import {
  BLOCK_TEXT,
  CHECK_TEXT,
  DROPPED_TEXT,
  EXCLUDE_TEXT,
  HISTORY_STATUS_TEXT,
  HOW_TAG,
  IMPORT_DATE_FORMAT,
  IMPORT_SENTENCE,
  IMPORT_TEXT,
  NOTE_TEXT,
  POINT_BAD_TEXT,
  POINT_TAG,
  REJECTION_TEXT,
  REJECTION_VARIANT_TEXT,
  TARGET_LABEL,
  WHY_TEXT,
  pointText,
} from './customerImportText.js'
import { formatKstIso } from './kstDate.js'
import { POLL_SLOW_MS, POLL_WATCH_CAP_MS, pollDelayOf } from './pollSchedule.js'
import { telText } from './telNumber.js'

// ─── 경로 · 액션 · 한도 (V1·V3·I3·I11) ──────────────────────────────────────────────────────────────────

/**
 * 화면 경로 — appserver SQL 057 의 메뉴 `sys_resources.url` 과 **같은 글자**다(바꾸면 그 SQL 도). POS 환경설정 「고객 가져오기」
 * 링크(pos2-lite `OrdernAdminPage`)도 이 경로를 매직링크 `page` 로 연다 — 토큰 로그인은 **등록 라우트면** 받으므로 라우트를
 * 등록하는 것이 곧 관리자 웹 쪽 「열 수 있는 페이지에 더하기」다(`loginRedirect.safeRedirectPage`).
 */
export const CUSTOMER_IMPORT_PATH = '/v/business/telInfo/import'

/** 가져오기 API base(`/api` 밖 세션 체인 — I1) */
export const CUSTOMER_IMPORT_API = '/telInfo/import'

/**
 * 세션 액션 id — SQL 057 `sys_action.action_id` 와 같은 글자(I11). 화면 게이트(`checkAuthActionAccess`·`permittedRead`)가 이것만
 * 본다: 액션이 없으면 **부르지 않는다**(보안 계층의 403 은 이 앱에서 강제 로그아웃이다). 그래서 이 화면이 SQL 057 보다 먼저
 * 나가도 기능이 숨을 뿐 세션이 끊기지 않는다.
 */
export const CUSTOMER_IMPORT_ACTION = Object.freeze({
  /** `GET /telInfo/import` — 가져온 기록. 라우트 게이트(`meta.requiredAction`)이기도 하다 — 화면이 반드시 부르는 조회다 */
  LIST: 'TelInfoImportList',
  /** `GET /telInfo/import/*` — 작업 보기 */
  GET: 'TelInfoImportGet',
  /** `POST /telInfo/import` — 파일 올리기 */
  UPLOAD: 'TelInfoImportUpload',
  /** `POST /telInfo/import/* /mapping` — 칸 확정 */
  MAPPING: 'TelInfoImportMapping',
  /** `POST /telInfo/import/* /options` — 고르기 */
  OPTIONS: 'TelInfoImportOptions',
  /** `POST /telInfo/import/* /apply` — 가져오기·포인트 다시 시도 */
  APPLY: 'TelInfoImportApply',
  /** `POST /telInfo/import/* /cancel` — 다른 파일 */
  CANCEL: 'TelInfoImportCancel',
})

/** 작업 하나의 쓰기 경로 조각(I5~I8) */
export const IMPORT_STEP = Object.freeze({ MAPPING: 'mapping', OPTIONS: 'options', APPLY: 'apply', CANCEL: 'cancel' })

/** 한 번에 올릴 수 있는 크기 — 서버 I3 의 20MB(20,971,520 B)와 같은 값이다. 50MB 를 넘으면 앞단이 사유 없이 끊는다(AP4 §5-21) */
export const IMPORT_FILE_MAX_BYTES = 20 * 1024 * 1024

/** 고르기 창의 확장자 — 서버는 확장자가 아니라 앞 바이트로 가른다(I3). 고르기 편의일 뿐이다 */
export const IMPORT_ACCEPT = '.xls,.xlsx,.csv,.txt'

/** 올리기의 multipart 부분 이름(I3) — `FormData` 는 화면이 만든다 */
export const IMPORT_UPLOAD_PART = Object.freeze({ FILE: 'file', ATTESTED: 'attested', STORE_CD: 'storeCd' })

/** 가져온 기록의 한 장 크기(I9 — 앱 목록 규약 `pageSize`·`currPage`) */
export const IMPORT_HISTORY_PAGE_SIZE = 20

// ─── 어휘 (I2·I4·I10) ─────────────────────────────────────────────────────────────────────────────────

/** 작업 상태(I2) — `READING` 은 1차에 커밋되지 않는 예약 값이다(받으면 진행 중으로 본다) */
export const IMPORT_STATUS = Object.freeze({
  READING: 'READING',
  MAPPING: 'MAPPING',
  REVIEW: 'REVIEW',
  IMPORTING: 'IMPORTING',
  DONE: 'DONE',
  CANCELED: 'CANCELED',
  FAILED: 'FAILED',
})

/** 진행 국면(I2 `progress.phase`) */
export const IMPORT_PHASE = Object.freeze({ CLEANING: 'CLEANING', IMPORTING: 'IMPORTING', POINTS: 'POINTS' })

/** 보는 사람(I1 — 작업의 `viewer`, 가져온 기록의 `caller.viewer`) */
export const IMPORT_VIEWER = Object.freeze({ STORE: 'STORE', AGENCY: 'AGENCY' })

/** 고객 표의 탭(I4 `?tab=`) */
export const IMPORT_TAB = Object.freeze({ CHECK: 'check', ALL: 'all', OUT: 'out', SKIP: 'skip' })

/** 오더엔 칸(I10 `to`) — 칸 확인 고르기 상자의 순서. `drop` 은 고를 수 없다(I5) */
export const IMPORT_TARGETS = Object.freeze(Object.keys(TARGET_LABEL))

/** 정한 방법(I10 `how`) */
export const MAPPING_HOW = Object.freeze({ DICT: 'dict', CHECK: 'check', UNKNOWN: 'unknown', USER: 'user' })

/** 고객 한 명의 포인트 결과(I10 `points.act`) */
export const POINT_ACT = Object.freeze({
  NONE: 'NONE',
  NOMOBILE: 'NOMOBILE',
  PENDING: 'PENDING',
  ASK: 'ASK',
  OFF: 'OFF',
  NEW: 'NEW',
  ADD: 'ADD',
  SKIP: 'SKIP',
  ALREADY: 'ALREADY',
})

/**
 * 포인트를 옮기는 판정(새 회원·더하기) — 앱서버 `PointAct.moves` 와 같은 뜻이다. 옮길 값(`points.value`)은 이 판정에만 있다 — 파일 값이 줄마다
 * 다른데 옮기지 않는 고객(이미 옮김·건너뜀·옮기지 않음)은 서버가 값을 고르게 하지 않는다(`value` null).
 */
export function pointActMoves(act) {
  return act === POINT_ACT.NEW || act === POINT_ACT.ADD
}

/** 고르기(I6) */
export const POINTS_ANSWER = Object.freeze({ BALANCE: 'BALANCE', IGNORE: 'IGNORE' })
export const UNIT_ANSWER = Object.freeze({ WON: 'WON', SKIP: 'SKIP' })
export const EXISTING_POLICY = Object.freeze({ SKIP: 'SKIP', ADD: 'ADD' })

/** 「더하기」를 못 고르는 까닭(I4 `points.addBlocked`) */
export const ADD_BLOCKED = Object.freeze({ AGENCY: 'AGENCY', SAME_FILE: 'SAME_FILE' })

/** 주소 확인(Person `addresses[].check`) */
export const ADDRESS_CHECK = Object.freeze({ NAME_ONLY: 'NAME_ONLY', NOT_FOUND: 'NOT_FOUND' })

/** 가져오기를 누를 때 남은 일(I4 `preview.block.reason`) */
export const BLOCK_REASON = Object.freeze({
  NO_PEOPLE: 'NO_PEOPLE',
  POINT_QUESTIONS: 'POINT_QUESTIONS',
  POINT_CONFLICTS: 'POINT_CONFLICTS',
})

/** 남은 일이 있는 자리 — 화면이 그리로 옮긴다 */
export const BLOCK_TARGET = Object.freeze({ POINTS: 'points', PEOPLE: 'people' })

/** 거절의 형식 힌트·형식(I3 세부) */
const SPREADSHEET_XML_HINT = 'SPREADSHEET_XML'
const OLD_FORMATS = Object.freeze(['XLS', 'HTML'])

// ─── URL ──────────────────────────────────────────────────────────────────────────────────────────────

/** 작업 하나(I4) — `id` 가 없으면 목록·올리기(I3·I9) */
export function importUrl(id) {
  return id == null ? CUSTOMER_IMPORT_API : `${CUSTOMER_IMPORT_API}/${id}`
}

/** 작업 하나의 쓰기(I5~I8) — `step` 은 [IMPORT_STEP] */
export function importStepUrl(id, step) {
  return `${importUrl(id)}/${step}`
}

/**
 * 작업 보기·고르기의 질의(`?tab=&page=`) — 값이 없는 키는 빼고 보낸다(서버 기본: 확인할 것이 있으면 `check`, 0쪽). 고르기 응답이
 * **지금 보는 탭·쪽**을 그대로 실어 오게 화면이 같은 값을 싣는다(I6).
 */
export function importViewParams(tab, page) {
  return Object.assign({}, tab ? { tab } : null, typeof page === 'number' && page >= 0 ? { page } : null)
}

// ─── 단계 (§1-3 b) ────────────────────────────────────────────────────────────────────────────────────

/** 화면 단계 — [stageOf] 의 답 */
export const IMPORT_STAGE = Object.freeze({
  /** 보는 사람을 아직 모른다(가져온 기록 첫 읽기 전) */
  LOADING: 'LOADING',
  PICK_STORE: 'PICK_STORE',
  PICK: 'PICK',
  UPLOADING: 'UPLOADING',
  MAPPING: 'MAPPING',
  CLEANING: 'CLEANING',
  REVIEW: 'REVIEW',
  IMPORTING: 'IMPORTING',
  DONE: 'DONE',
  FAILED: 'FAILED',
})

/**
 * **지금 무엇을 그리는가 — 한 곳.** 서버 작업 보기(`view`)와 화면 상태(`ui`)로 정한다.
 *
 * - 올리는 요청이 나가 있으면 `UPLOADING`(서버는 읽기까지 그 요청 안에서 끝낸다 — I3).
 * - `MAPPING` 은 칸 확인이다. 뒤에서 미리 정리 중인 `progress` 는 보지 않는다(칸을 안 바꾸면 그 결과를 서버가 그대로 쓴다).
 * - `READING`(1차엔 오지 않는 예약 값)과 `REVIEW` 의 정리 중(`progress` 있음 · `preview` 없음)은 `CLEANING`.
 * - `REVIEW` 에서 「칸 다시 보기」(`ui.remapping`)를 누르면 요청 없이 칸 확인을 다시 그린다.
 * - 작업이 없거나 그만둔(`CANCELED`) 작업이면 첫 화면 — 사업장 계정이 매장을 아직 안 골랐으면 `PICK_STORE`, 보는 사람을 아직
 *   모르면 `LOADING`.
 *
 * @param {?object} view 작업 보기(I4) — 없으면 `null`
 * @param {{viewer?: ?string, storeCd?: *, uploading?: boolean, remapping?: boolean}} ui
 * @returns {string} [IMPORT_STAGE] 의 하나
 */
export function stageOf(view, ui) {
  const state = ui || {}
  if (state.uploading) return IMPORT_STAGE.UPLOADING

  switch (view && view.status) {
    case IMPORT_STATUS.READING:
      return IMPORT_STAGE.CLEANING
    case IMPORT_STATUS.MAPPING:
      return IMPORT_STAGE.MAPPING
    case IMPORT_STATUS.REVIEW:
      if (view.preview == null || view.progress != null) return IMPORT_STAGE.CLEANING
      return state.remapping ? IMPORT_STAGE.MAPPING : IMPORT_STAGE.REVIEW
    case IMPORT_STATUS.IMPORTING:
      return IMPORT_STAGE.IMPORTING
    case IMPORT_STATUS.DONE:
      return IMPORT_STAGE.DONE
    case IMPORT_STATUS.FAILED:
      return IMPORT_STAGE.FAILED
    default:
      if (!state.viewer) return IMPORT_STAGE.LOADING
      if (state.viewer === IMPORT_VIEWER.AGENCY && (state.storeCd == null || state.storeCd === '')) {
        return IMPORT_STAGE.PICK_STORE
      }
      return IMPORT_STAGE.PICK
  }
}

/** 단계 줄(「파일 올리기 · 칸 확인 · 미리보기 · 끝」)의 지금 칸(0-base) — 그리지 않는 단계(읽는 중·실패)는 `null` */
const STEP_OF_STAGE = Object.freeze({
  PICK_STORE: 0,
  PICK: 0,
  UPLOADING: 1,
  MAPPING: 1,
  CLEANING: 2,
  REVIEW: 2,
  IMPORTING: 3,
  DONE: 3,
})

export function stepOf(stage) {
  return Object.prototype.hasOwnProperty.call(STEP_OF_STAGE, stage) ? STEP_OF_STAGE[stage] : null
}

/** 서버 진행을 지켜보는(폴링하는) 단계인가 */
export function pollsIn(stage) {
  return stage === IMPORT_STAGE.CLEANING || stage === IMPORT_STAGE.IMPORTING
}

// ─── 폴링 (§1-3 e) ───────────────────────────────────────────────────────────────────────────────────

/**
 * 폴링 회차의 열쇠 — 단계와 진행 국면. **열쇠가 바뀌면 시계를 0 으로 되돌린다**(정리 → 미리보기, 고객 → 포인트): 국면마다 「사람이
 * 서서 보는 시간」이 새로 시작된다.
 */
export function pollKeyOf(stage, view) {
  const phase = (view && view.progress && view.progress.phase) || ''
  return `${stage}:${phase}`
}

/**
 * **다음 조회까지의 대기 — 가져오기 폴링 규칙 한 곳.** 간격은 `pollSchedule` 그대로(30초 안 3초 · 그 뒤 10초)이고, 상한
 * (`POLL_WATCH_CAP_MS` 300초) 뒤의 규칙만 이 화면의 것이다(I2): 서버가 「멈추지 않았다」(`progress.stalled === false`)고 말하는
 * 동안은 10초로 계속 본다 — 큰 파일의 포인트 쓰기는 분 단위다. 멈춤(`stalled`)이거나 진행을 모르면(마지막 읽기 실패 — `null`)
 * **멈춘다**(`null`) — 화면은 「확인을 멈췄어요 · 다시 확인」을 말한다.
 *
 * @param {number} elapsedMs 이 회차의 시작(또는 다시 확인)부터
 * @param {?object} progress 마지막으로 읽은 진행(I2) — 마지막 읽기가 실패했으면 `null`
 * @returns {?number} 대기(ms), 멈추면 `null`
 */
export function importPollNext(elapsedMs, progress) {
  if (elapsedMs < POLL_WATCH_CAP_MS) return pollDelayOf(elapsedMs)
  return progress && progress.stalled === false ? POLL_SLOW_MS : null
}

/**
 * 폴링의 한 줄 — 멈췄으면 「확인을 멈췄어요 · 마지막 진행 HH:mm」(+ 화면의 「다시 확인」), 서버가 잠시 멈췄으면(`stalled`)
 * 「잠시 멈췄어요 — 서버가 곧 이어서 처리해요」. 말할 것이 없으면 `null`.
 *
 * @param {{progress: ?object, stopped: boolean}} poll
 * @returns {?{text: string, stopped: boolean}}
 */
export function pollLineOf(poll) {
  const { progress, stopped } = poll || {}
  const time = progress && formatKstIso(IMPORT_DATE_FORMAT.TIME, progress.updatedAt)

  if (stopped) return { text: time ? IMPORT_SENTENCE.stopped(time) : IMPORT_TEXT.stoppedPlain, stopped: true }
  if (progress && progress.stalled) return { text: IMPORT_SENTENCE.stalled(time || ''), stopped: false }
  return null
}

/** 진행 → 막대(0~100). 모르면 `null` — 거짓 진행을 그리지 않는다 */
function percentOf(done, total) {
  return typeof done === 'number' && typeof total === 'number' && total > 0
    ? Math.min(100, Math.floor((done * 100) / total))
    : null
}

/**
 * 진행 카드(올리기·정리·가져오기) — **올리기 %와 서버 `progress` 만** 그린다(시안의 단계 체크 애니메이션은 흉내라 옮기지 않는다).
 *
 * @param {string} stage
 * @param {?object} view
 * @param {{percent?: number}} upload 올리는 요청의 진행
 * @returns {{title: string, count: ?string, percent: ?number, note: string}}
 */
export function workingOf(stage, view, upload) {
  if (stage === IMPORT_STAGE.UPLOADING) {
    const percent = Math.max(0, Math.min(100, Math.round((upload && upload.percent) || 0)))
    return {
      title: percent >= 100 ? IMPORT_TEXT.readingTitle : IMPORT_SENTENCE.uploadPercent(percent),
      count: null,
      percent,
      note: IMPORT_TEXT.uploadingNote,
    }
  }

  const progress = (view && view.progress) || null
  const known = !!progress && typeof progress.done === 'number' && typeof progress.total === 'number'
  const importing = stage === IMPORT_STAGE.IMPORTING
  const points = importing && progress && progress.phase === IMPORT_PHASE.POINTS
  const title = !importing
    ? IMPORT_TEXT.cleaningTitle
    : points
    ? IMPORT_TEXT.pointsMovingTitle
    : IMPORT_TEXT.importingTitle

  return {
    title,
    count: known
      ? IMPORT_SENTENCE.progressCount(progress.done, progress.total, importing ? IMPORT_SENTENCE.peopleUnit : '')
      : null,
    percent: known ? percentOf(progress.done, progress.total) : null,
    note: IMPORT_TEXT.workingNote,
  }
}

// ─── 칸 확인 (§1-3 d MAPPING · I5) ───────────────────────────────────────────────────────────────────

/**
 * 그 칸의 「정한 방법」 — 화면에서 사전 값과 다르게 골랐으면 `user`(고침). 사전 값으로 되돌렸는데 서버가 이미 `user` 라고
 * 말한 칸(저장한 고침)은 사전이 처음에 뭐라 했는지 응답에 없으므로 `dict` 로 그린다 — 그 판정(사전의 표)은 서버 것이라 사본을
 * 두지 않는다.
 */
export function howOf(column, to) {
  if (to !== column.dictTo) return MAPPING_HOW.USER
  return column.how === MAPPING_HOW.USER ? MAPPING_HOW.DICT : column.how
}

/** 화면에서 그 칸을 바꿨나 — 고친 값이 서버의 지금 값과 다를 때만 고친 칸이다([withMappingEdit] 가 같은 값을 지운다) */
function editedTo(edits, col) {
  return edits && Object.prototype.hasOwnProperty.call(edits, col) ? edits[col] : undefined
}

/**
 * 칸 하나를 고친 뒤의 고친 칸 표(`{col: to}`) — 서버의 지금 값으로 되돌리면 표에서 빠진다(「바꾼 것이 없다」가 정확히 빈 표다).
 * 순수 — 받은 표는 그대로다.
 */
export function withMappingEdit(mapping, edits, col, to) {
  const column = ((mapping && mapping.columns) || []).find((c) => c.col === col)
  const rest = Object.keys(edits || {})
    .filter((key) => String(key) !== String(col))
    .reduce((acc, key) => Object.assign(acc, { [key]: edits[key] }), {})

  return column && to !== column.to ? Object.assign(rest, { [col]: to }) : rest
}

/** 화면에서 바꾼 칸이 있나 */
export function mappingEdited(edits) {
  return Object.keys(edits || {}).length > 0
}

/**
 * 칸 확인 표의 줄 — **펼침** = 사전이 뜻 확인·모르는 칸·고침이라고 한 칸 + 화면에서 바꾼 칸, 나머지는 「나머지 n칸 · 보기」로 접는다.
 * 읽을 때 버린 칸(`dropped`)은 표에 없고 한 줄씩 말한다.
 *
 * @returns {{top: Array, rest: Array, checkCount: number, unknownCount: number, dropped: Array<string>, hasTel: boolean}}
 */
export function mappingRowsOf(mapping, edits) {
  const columns = (mapping && mapping.columns) || []
  const rows = columns.map((column) => {
    const edited = editedTo(edits, column.col)
    const to = edited === undefined ? column.to : edited
    const how = howOf(column, to)
    const tag = HOW_TAG[how] || HOW_TAG.dict

    return {
      col: column.col,
      header: column.header,
      example: (column.examples || []).join(' · '),
      to,
      how,
      howText: tag.text,
      howType: tag.type,
      // 까닭은 사전의 말이라 고친 칸에서는 보이지 않는다(시안과 같다)
      why: how === MAPPING_HOW.USER ? '' : WHY_TEXT[column.why] || '',
      edited: edited !== undefined,
      open: edited !== undefined || [MAPPING_HOW.CHECK, MAPPING_HOW.UNKNOWN, MAPPING_HOW.USER].indexOf(column.how) > -1,
    }
  })

  return {
    top: rows.filter((row) => row.open),
    rest: rows.filter((row) => !row.open),
    checkCount: columns.filter((column) => column.how === MAPPING_HOW.CHECK).length,
    unknownCount: columns.filter((column) => column.how === MAPPING_HOW.UNKNOWN).length,
    dropped: ((mapping && mapping.dropped) || []).map((item) =>
      IMPORT_SENTENCE.droppedLine(item.header, DROPPED_TEXT[item.reason] || '')
    ),
    hasTel: !!(mapping && mapping.hasTel),
  }
}

/** 칸 고르기 상자의 항목 — 오더엔 칸 열둘(I10 `to`) */
export function targetOptionsOf() {
  return IMPORT_TARGETS.map((to) => ({ value: to, label: TARGET_LABEL[to] }))
}

/**
 * 칸 확정 본문(I5) — **버린 칸을 뺀 모든 칸을 한 번씩**(빠지거나 겹치면 서버 400). 고친 칸은 고친 값, 나머지는 서버의 지금 값.
 * 전화번호 칸이 없으면 서버가 `NO_TEL_COLUMN` 으로 답한다 — 같은 규칙을 화면이 다시 적지 않는다.
 */
export function mappingBodyOf(mapping, edits) {
  return {
    columns: ((mapping && mapping.columns) || []).map((column) => {
      const edited = editedTo(edits, column.col)
      return { col: column.col, to: edited === undefined ? column.to : edited }
    }),
  }
}

// ─── 고르기 (I6) ──────────────────────────────────────────────────────────────────────────────────────

/** 서버의 지금 고르기(`view.options`)를 빠진 키 없는 모양으로 — 키 없음 = 기본값(질문 `null` · `SKIP` · 빈 목록) */
function normalizedOptions(options) {
  const source = options || {}
  return {
    pointsAnswer: source.pointsAnswer || null,
    unitAnswer: source.unitAnswer || null,
    existingPolicy: source.existingPolicy || EXISTING_POLICY.SKIP,
    conflicts: Array.isArray(source.conflicts) ? source.conflicts : [],
    skipTels: Array.isArray(source.skipTels) ? source.skipTels : [],
    keepRows: Array.isArray(source.keepRows) ? source.keepRows : [],
  }
}

/** 목록에 있으면 빼고 없으면 더한다 — 새 배열 */
const toggled = (list, value) =>
  list.indexOf(value) > -1 ? list.filter((item) => item !== value) : list.concat([value])

/** 질문 1 — 「손님이 지금 바로 쓸 수 있는 금액인가요?」 */
export const withPointsAnswer = (answer) => (options) => Object.assign({}, options, { pointsAnswer: answer })

/** 질문 2 — 「‘칸’ 칸의 1이 1원인가요?」 */
export const withUnitAnswer = (answer) => (options) => Object.assign({}, options, { unitAnswer: answer })

/** 이미 포인트 회원인 번호 — 건너뛰기·더하기 */
export const withExisting = (policy) => (options) => Object.assign({}, options, { existingPolicy: policy })

/** 고객 빼기·다시 넣기 */
export const withSkip = (tel) => (options) => Object.assign({}, options, { skipTels: toggled(options.skipTels, tel) })

/** 050 줄 살리기·다시 빼기 */
export const withKeepRow = (row) => (options) =>
  Object.assign({}, options, { keepRows: toggled(options.keepRows, row) })

/**
 * 값이 다른 같은 번호의 포인트 고르기 — 번호마다 하나다. `value` 가 `null` 이면 고른 값을 지운다(「다시 고르기」). 목록이지
 * 번호를 키로 한 맵이 아니다(4xx 본문 로그 가림이 칸 이름 `tel` 로 걸리게 — I6·AP4 §5-19).
 */
export const withConflict = (tel, value) => (options) =>
  Object.assign({}, options, {
    conflicts: options.conflicts.filter((item) => item.tel !== tel).concat(value == null ? [] : [{ tel, value }]),
  })

/**
 * **고르기 본문(I6) — 고른 것 전체.** 서버의 지금 고르기에 고르기 하나(`change` — 위 `with*` 의 하나)를 얹는다. 질문 1 이
 * 「잔액」이 아니면 질문 2 는 `null` 로 보낸다(서버도 그렇게 맞춘다). 순수 — 받은 `options` 는 그대로다.
 *
 * @param {?object} options 서버의 `view.options`
 * @param {?function(object): object} change 고르기 하나
 */
export function optionsBodyOf(options, change) {
  const base = normalizedOptions(options)
  const next = typeof change === 'function' ? change(base) : base

  return {
    pointsAnswer: next.pointsAnswer,
    unitAnswer: next.pointsAnswer === POINTS_ANSWER.BALANCE ? next.unitAnswer : null,
    existingPolicy: next.existingPolicy,
    conflicts: next.conflicts.slice(),
    skipTels: next.skipTels.slice(),
    keepRows: next.keepRows.slice(),
  }
}

// ─── 미리보기 (§1-3 d REVIEW) ─────────────────────────────────────────────────────────────────────────

/** 이름, 없으면 다 친 번호의 표기(`telNumber.telText`) */
const who = (person) => person.name || telText(person.tel)

/** 확인할 것 ② — 사전이 뜻 확인·모르는 칸이라 한 칸과 고친 칸(저장된 칸 맞춤 기준) */
function checkedColumnsOf(mapping) {
  return ((mapping && mapping.columns) || []).filter(
    (column) => [MAPPING_HOW.CHECK, MAPPING_HOW.UNKNOWN, MAPPING_HOW.USER].indexOf(column.how) > -1
  )
}

/** 확인할 것 ③ — 포인트 줄의 네 갈래(시안 `pointsLine`) */
function pointsLineOf(points) {
  if (!points || !points.on) return IMPORT_TEXT.pointsNoColumn
  if (!points.answered) {
    return IMPORT_SENTENCE.pointsAsk(IMPORT_SENTENCE.pointsHeadline(points.column, points.withPts, points.maxPts))
  }
  if (!points.moving) return IMPORT_TEXT.pointsOff

  return IMPORT_SENTENCE.pointsMoving(
    (points.members || 0) + (points.added || 0),
    points.pointsSum,
    (points.pending || []).length,
    points.notMoved
  )
}

/**
 * **확인할 것 세 줄**(I4 「확인할 것 세 줄」 칸 그대로) + 같은 파일 배너.
 *
 * @returns {?{sameFile: ?string, items: Array<{key: string, label: string, text: string}>}} 미리보기가 없으면 `null`
 */
export function checklistOf(view) {
  const preview = view && view.preview
  if (!preview) return null

  const summary = preview.summary || {}
  const columns = checkedColumnsOf(view.mapping)

  return {
    sameFile: view.sameFile
      ? IMPORT_SENTENCE.sameFile(formatKstIso(IMPORT_DATE_FORMAT.DAY, view.sameFile.doneAt) || '')
      : null,
    items: [
      {
        key: 'count',
        label: IMPORT_TEXT.checkCountLabel,
        text: IMPORT_SENTENCE.countLine(summary.read, summary.people, summary.excluded, summary.skipped),
      },
      {
        key: 'columns',
        label: IMPORT_SENTENCE.checkedColumns(columns.length),
        text: columns
          .map((column) => IMPORT_SENTENCE.columnPair(column.header, TARGET_LABEL[column.to] || column.to))
          .join(' · '),
      },
      { key: 'points', label: IMPORT_TEXT.checkPointsLabel, text: pointsLineOf(preview.points) },
    ],
  }
}

/** 이미 포인트 회원 줄(STORE 는 회원마다 지금·파일 값과 「더하기」, 대리점·같은 파일은 건너뜀 한 줄) */
function existingOf(points, options) {
  const count = points.existingCount || 0
  if (!count) return null

  const lead = IMPORT_SENTENCE.existingLead(count)
  if (points.addBlocked === ADD_BLOCKED.AGENCY) return { text: lead + IMPORT_TEXT.existingAgency, choices: null }
  if (points.addBlocked === ADD_BLOCKED.SAME_FILE) return { text: lead + IMPORT_TEXT.existingSameFile, choices: null }

  const sample = points.existingSample || []
  const more = count > sample.length ? IMPORT_SENTENCE.more(count - sample.length) : ''
  const named = (person) => person.exName || who(person)
  const adding = options.existingPolicy === EXISTING_POLICY.ADD

  return {
    text:
      lead +
      sample.map((person) => IMPORT_SENTENCE.existingItem(named(person), person.exPoints, person.value)).join(' · ') +
      more,
    choices: points.addAllowed
      ? [
          { value: EXISTING_POLICY.SKIP, label: IMPORT_TEXT.existingSkip },
          {
            value: EXISTING_POLICY.ADD,
            label: IMPORT_SENTENCE.addChoice(sample.map((person) => pointText(person.after)).join(' · ') + more),
          },
        ]
      : null,
    value: options.existingPolicy,
    warning: points.addAllowed && adding ? IMPORT_TEXT.addWarning : null,
  }
}

/**
 * **포인트 카드**(`points.on` 일 때) — 질문 둘, 옮길 것·건너뛸 것·못 옮기는 것. 서버가 보는 사람(`viewer`)에 맞춰 칸을 비워 주므로
 * (대리점이면 오더엔 잔액 `null`·`addBlocked: AGENCY` — 결정 25) 여기서 역할로 다시 가리지 않는다.
 *
 * @param {?object} view
 * @returns {?object} 포인트 칸이 없으면 `null`
 */
export function pointsCardOf(view) {
  const points = view && view.preview && view.preview.points
  if (!points || !points.on) return null

  const options = normalizedOptions(view.options)
  const balance = options.pointsAnswer === POINTS_ANSWER.BALANCE
  const newSample = points.newSample || []
  const members = points.members || 0
  const pending = (points.pending || []).length

  return {
    headline: IMPORT_SENTENCE.pointsHeadline(points.column, points.withPts, points.maxPts),
    pointsQuestion: {
      text: IMPORT_TEXT.pointsQuestion,
      value: options.pointsAnswer,
      choices: [
        { value: POINTS_ANSWER.BALANCE, label: IMPORT_TEXT.pointsAnswerBalance },
        { value: POINTS_ANSWER.IGNORE, label: IMPORT_TEXT.pointsAnswerIgnore },
      ],
    },
    unitQuestion: balance
      ? {
          text: IMPORT_SENTENCE.unitQuestion(points.column),
          value: options.unitAnswer,
          choices: [
            { value: UNIT_ANSWER.WON, label: IMPORT_TEXT.unitAnswerWon },
            { value: UNIT_ANSWER.SKIP, label: IMPORT_TEXT.unitAnswerSkip },
          ],
        }
      : null,
    result: points.moving
      ? {
          newLine: IMPORT_SENTENCE.newMembers(members, points.newSum),
          newSample: newSample.length
            ? newSample.map((person) => IMPORT_SENTENCE.memberValue(who(person), person.value)).join(' · ') +
              (members > newSample.length ? IMPORT_SENTENCE.more(members - newSample.length) : '')
            : '',
          existing: existingOf(points, options),
          lines: [
            points.already ? { text: IMPORT_SENTENCE.already(points.already), warn: false } : null,
            pending ? { text: IMPORT_SENTENCE.pending(pending), warn: true } : null,
            { text: IMPORT_SENTENCE.notMoved(points.notMoved || 0, points.notMovedSum), warn: false },
            points.memberBefore != null && points.memberAfter != null
              ? { text: IMPORT_SENTENCE.memberCount(points.memberBefore, points.memberAfter), warn: false }
              : null,
            points.memberFactsUnknown ? { text: IMPORT_TEXT.memberFactsUnknown, warn: true } : null,
          ].filter(Boolean),
        }
      : null,
    note: IMPORT_TEXT.pointsNote,
  }
}

/**
 * 주소 한 줄(주소 → 오더엔) — 세 갈래: 찾은 건물(이름 + 동호, 아래 도로명) · 이름만 맞은 건물(**글자 주소로 넣는다** — 후보를
 * 보이고 첫 전화 때 POS 「건물 찾기」, 메인 결정 ④) · 못 찾음(글자 그대로).
 *
 * @returns {{text: string, sub: ?string, warn: boolean}}
 */
export function addressLineOf(address) {
  const item = address || {}
  const join = (parts) => parts.filter(Boolean).join(' ')
  const place = item.place

  if (place) {
    return place.name
      ? { text: join([place.name, item.detail]), sub: place.roadShort || null, warn: false }
      : { text: join([place.roadShort, item.detail]), sub: null, warn: false }
  }

  const text = join([item.text, item.detail])
  if (item.check === ADDRESS_CHECK.NAME_ONLY && item.candidate) {
    return { text, sub: IMPORT_SENTENCE.addressNameOnly(item.candidate.name), warn: true }
  }
  return { text, sub: IMPORT_TEXT.addressNotFound, warn: true }
}

/** 고객 한 명의 포인트 칸 */
function pointCellOf(points) {
  const cell = points || {}
  const values = cell.values || []
  const tag = POINT_TAG[cell.act] || POINT_TAG.NONE

  if (cell.act === POINT_ACT.PENDING) {
    return { kind: 'pick', choices: values.map((value) => ({ value, label: pointText(value) })) }
  }
  if (values.length) {
    // 「더하기」는 지금 잔액과 더한 값을 보인다 — 그 둘은 매장 계정에게만 실린다(대리점이면 `null`)
    const adding = cell.act === POINT_ACT.ADD && cell.exPoints != null && cell.after != null
    // 옮기지 않는 고객의 값이 줄마다 다르면 서버가 고른 값이 없다 — 파일 값들을 그대로 보이고 다시 고르기를 두지 않는다(고를 것이 없다)
    return {
      kind: 'value',
      valueText: cell.value != null ? pointText(cell.value) : values.map(pointText).join(' · '),
      tagText: adding ? IMPORT_SENTENCE.addPoints(cell.exPoints, cell.after) : tag.text,
      warn: tag.type === 'warn',
      repick: pointActMoves(cell.act) && values.length > 1,
    }
  }
  if (cell.bad) return { kind: 'bad', text: POINT_BAD_TEXT[cell.bad] || cell.bad }
  return { kind: 'none' }
}

/**
 * 고객 표 한 줄(Person — I4). 메모는 매장 계정이면 오더엔 메모에 덧붙인 결과(`memoAfter`), 대리점이면 파일 메모(`memo`)다 —
 * 대리점에게는 서버가 오더엔 값을 `null` 로 준다(결정 25). `exName` 도 같은 이유로 대리점에게는 없다(`null` 이면 그리지 않는다).
 * 번호는 다 친 번호의 표기(`telNumber.telText`)로 보이고, 고르기에는 받은 번호(`tel`) 그대로 싣는다.
 */
export function personRowOf(person, viewer) {
  const item = person || {}
  const memo = viewer === IMPORT_VIEWER.STORE ? item.memoAfter : item.memo
  const prefs = item.prefs || []
  const rider = item.rider || []

  return {
    tel: item.tel,
    telText: telText(item.tel),
    rowsText: IMPORT_SENTENCE.fileRows(item.rows),
    nameText: item.name || IMPORT_TEXT.noName,
    named: !!item.name,
    exNameText: item.ex && item.exName && item.exName !== item.name ? IMPORT_SENTENCE.exName(item.exName) : null,
    addresses: (item.addresses || []).map(addressLineOf),
    memoText: [memo].concat(prefs.map(IMPORT_SENTENCE.pref)).filter(Boolean).join(' · '),
    riderText: rider.length ? IMPORT_SENTENCE.rider(rider) : null,
    points: pointCellOf(item.points),
    resultText: item.ex ? IMPORT_TEXT.mergedCustomer : IMPORT_TEXT.newCustomer,
    merged: !!item.ex,
    // 모르는 어휘는 그 낱말을 그대로 보인다 — 숨기면 확인할 것이 조용히 사라진다(어휘 대조는 단위 시험이 한다)
    checks: (item.checks || []).map((code) => CHECK_TEXT[code] || code),
    notes: (item.notes || []).map((code) => NOTE_TEXT[code] || code),
    skipped: !!item.skipped,
    skipLabel: item.skipped ? IMPORT_TEXT.unskip : IMPORT_TEXT.skip,
  }
}

/** 제외 탭 한 줄(ExcludedRow — I4) — 살린 050 줄도 이 탭에 있다 */
export function excludedRowOf(row) {
  const item = row || {}
  return {
    row: item.row,
    nameText: item.name || IMPORT_TEXT.noName,
    rawTel: item.rawTel || IMPORT_TEXT.none,
    reasonText: item.revived ? IMPORT_TEXT.revived : EXCLUDE_TEXT[item.reason] || item.reason || '',
    revived: !!item.revived,
    actionLabel: item.canRevive ? (item.revived ? IMPORT_TEXT.unrevive : IMPORT_TEXT.revive) : null,
  }
}

/** 고객 표의 탭 — 확인할 것 · 전체 · 제외 n줄 · (뺀 고객이 있으면) 뺀 고객 */
export function tabsOf(preview) {
  const summary = (preview && preview.summary) || {}
  return [
    { key: IMPORT_TAB.CHECK, label: IMPORT_SENTENCE.tabCheck(summary.check || 0) },
    { key: IMPORT_TAB.ALL, label: IMPORT_SENTENCE.tabAll(summary.people || 0) },
    { key: IMPORT_TAB.OUT, label: IMPORT_SENTENCE.tabOut(summary.excluded || 0) },
  ].concat(summary.skipped ? [{ key: IMPORT_TAB.SKIP, label: IMPORT_SENTENCE.tabSkip(summary.skipped) }] : [])
}

/**
 * **고객 표의 자리(탭·쪽) — 탭 고르기의 한 곳.** 탭 줄이 고른 것으로 보이는 탭도, 줄 차례가 온 표 읽기가 읽을 자리도 여기서 나온다.
 *
 * - 사람이 고른 자리(`want` — 마지막에 누른 탭·쪽)가 이긴다. 누른 탭은 응답 전에 곧바로 고른 것으로 보인다.
 * - 고른 자리가 없으면 서버가 준 표의 자리다.
 * - 그 탭이 보이는 탭([tabsOf])에 없으면 — 「뺀 고객」 탭에서 마지막 고객을 다시 넣으면 그 탭이 사라진다 — **기본 탭**이다. 기본은
 *   서버가 정한다(`tab` 없이 읽는다 — 확인할 것이 있으면 `check`, 없으면 `all`, I4). 화면은 그 규칙을 다시 적지 않는다.
 *
 * @param {?object} preview 작업 보기의 `preview`
 * @param {?{tab: string, page: number}} want 사람이 고른 자리 — 없으면 `null`
 * @returns {{tab: ?string, page: number}} `tab` 이 `null` 이면 기본 탭(서버가 정한다)
 */
export function tablePlaceOf(preview, want) {
  const table = (preview && preview.table) || {}
  const shown = (tab) => tabsOf(preview).some((item) => item.key === tab)

  if (want && shown(want.tab)) return { tab: want.tab, page: want.page }
  if (shown(table.tab)) return { tab: table.tab, page: table.page }
  return { tab: null, page: 0 }
}

/**
 * **줄 차례가 온 표 읽기의 질의** — 표가 이미 그 자리([tablePlaceOf])면 `null`(읽지 않는다). 표 읽기는 요청 줄(`requestLane`)에
 * 서서 **차례가 온 뒤에** 이것을 묻는다. 그래서 앞 요청을 기다리는 동안 탭을 몇 번 눌렀든 마지막에 누른 자리 하나만 읽고, 앞 읽기나
 * 고르기 응답이 이미 그 자리를 그렸으면 아무것도 읽지 않는다 — 줄에 선 탭 읽기가 하나로 합쳐진다.
 *
 * @param {?object} preview
 * @param {?{tab: string, page: number}} want
 * @returns {?object} [importViewParams] 의 질의, 읽을 것이 없으면 `null`
 */
export function tableReadOf(preview, want) {
  const table = preview && preview.table
  if (!table) return null

  const place = tablePlaceOf(preview, want)
  return place.tab === table.tab && place.page === table.page ? null : importViewParams(place.tab, place.page)
}

/**
 * **가져오기를 누를 때 남은 일** → 바닥 줄 문장과 옮겨 갈 자리. 판정은 서버의 `preview.block` 이다 — 바닥 버튼은 늘 눌리고, 남은
 * 일이 있으면 요청 없이 이 문장을 적고 그 자리로 옮긴다(시안 `importBlock`·`IMPORT_APPLY`).
 *
 * @returns {?{text: string, target: string, tab: ?string}}
 */
export function blockNoticeOf(block) {
  switch (block && block.reason) {
    case BLOCK_REASON.NO_PEOPLE:
      return { text: BLOCK_TEXT.NO_PEOPLE, target: BLOCK_TARGET.PEOPLE, tab: IMPORT_TAB.CHECK }
    case BLOCK_REASON.POINT_QUESTIONS:
      return { text: BLOCK_TEXT.POINT_QUESTIONS, target: BLOCK_TARGET.POINTS, tab: null }
    case BLOCK_REASON.POINT_CONFLICTS: {
      const names = (block.names || []).filter(Boolean).join(', ')
      return {
        text: names ? IMPORT_SENTENCE.pointConflicts(names) : BLOCK_TEXT.POINT_CONFLICTS,
        target: BLOCK_TARGET.PEOPLE,
        tab: IMPORT_TAB.CHECK,
      }
    }
    default:
      return null
  }
}

/** 바닥 버튼 — 매장 이름이 든다(확인 창 없음 — 대리점이 매장을 잘못 고른 것도 버튼에서 보인다, review-U2 M6) */
export function applyLabelOf(view) {
  const summary = (view && view.preview && view.preview.summary) || {}
  return IMPORT_SENTENCE.applyLabel((view && view.storeNm) || '', summary.people || 0)
}

/** 바닥 줄 왼쪽 — 알릴 것(남은 일·거절)이 있으면 그것, 없으면 새 고객·합침 수 */
export function footLineOf(view, notice) {
  if (notice) return { text: notice, calm: false }
  const summary = (view && view.preview && view.preview.summary) || {}
  return { text: IMPORT_SENTENCE.footSummary(summary.created || 0, summary.merged || 0), calm: true }
}

// ─── 끝 · 실패 · 가져온 기록 ─────────────────────────────────────────────────────────────────────────

/**
 * 끝 화면(I4 `result`) — 가져올 때 번호마다 다시 본 결과라 미리보기 수와 다를 수 있다. 0 이 아닌 것만 한 줄로 더 말하고, 포인트를
 * 못 옮긴 회원이 있으면 「다시 시도」(I7 — 본문 `{}`)를 연다.
 */
export function doneOf(view) {
  const result = (view && view.result) || {}
  const agency = !!view && view.viewer === IMPORT_VIEWER.AGENCY
  const extras = [
    result.skippedMembers ? IMPORT_SENTENCE.doneSkippedMembers(result.skippedMembers) : null,
    result.already ? IMPORT_SENTENCE.doneAlready(result.already) : null,
    result.notMoved ? IMPORT_SENTENCE.doneNotMoved(result.notMoved) : null,
    result.memoCut ? IMPORT_SENTENCE.doneMemoCut(result.memoCut) : null,
  ].filter(Boolean)

  return {
    title: IMPORT_SENTENCE.doneTitle((view && view.storeNm) || '', result.people || 0),
    summary: IMPORT_SENTENCE.doneSummary(
      result.created || 0,
      result.merged || 0,
      (result.members || 0) + (result.added || 0),
      result.pointsSum || 0,
      result.excluded || 0,
      result.skipped || 0
    ),
    extras: extras.length ? extras.join(' · ') : null,
    pointsPending: result.pointsPending > 0 ? IMPORT_SENTENCE.pointsPending(result.pointsPending) : null,
    note: agency ? `${IMPORT_TEXT.doneNote} ${IMPORT_TEXT.doneNoteAgency}` : IMPORT_TEXT.doneNote,
    findHint: agency ? null : IMPORT_TEXT.doneFindHint,
    agency,
  }
}

/** 실패 화면 — 오류 번호만(정리가 세 번 연속 실패, I2) */
export function failedTextOf(view) {
  return IMPORT_SENTENCE.failed(view && view.failure ? view.failure.ref : null)
}

/**
 * 가져온 기록 한 줄(I9 item) — 누가·언제·몇 명. 올린 계정은 사업장이면 그 사업장 이름, 매장이면 「매장 계정」이다. 매장 열은
 * 사업장(대리점) 계정에게만 있다. `mine` 인 줄만 열 수 있다(작업 상세는 올린 계정만 — I1).
 */
export function historyRowOf(item, viewer) {
  const row = item || {}
  const actor = row.actor || {}
  const counted = row.people != null

  return {
    importId: row.importId,
    mine: !!row.mine,
    atText: formatKstIso(IMPORT_DATE_FORMAT.DAY_TIME, row.doneAt || row.createdAt) || IMPORT_TEXT.none,
    storeText: viewer === IMPORT_VIEWER.AGENCY ? row.storeNm || IMPORT_TEXT.none : null,
    actorText:
      actor.role === ADMIN_ROLE.CUST_MNG ? actor.name || IMPORT_TEXT.agencyActorFallback : IMPORT_TEXT.storeActor,
    peopleText: counted ? IMPORT_SENTENCE.historyCount(row.people) : IMPORT_TEXT.none,
    pointsText: counted
      ? IMPORT_SENTENCE.historyPoints((row.members || 0) + (row.added || 0), row.pointsSum || 0, row.pointsPending || 0)
      : IMPORT_TEXT.none,
    statusText: HISTORY_STATUS_TEXT[row.status] || '',
  }
}

// ─── 파일 고르기 · 올리기 (I3) ───────────────────────────────────────────────────────────────────────

/** 파일 고르는 칸 바로 위의 확인 줄 — 매장 계정은 「이 매장(…)이 쓰던」, 대리점은 「… 사장님이 맡긴」(조사 없는 꼴) */
export function attestTextOf(viewer, storeNm) {
  return viewer === IMPORT_VIEWER.AGENCY ? IMPORT_SENTENCE.attestAgency(storeNm) : IMPORT_SENTENCE.attestStore(storeNm)
}

/**
 * 올리기의 파일 밖 부분(이름·값 목록) — 파일을 고르면 곧바로 올리므로 확인 줄을 본 것이 곧 `attested=true` 다. 매장 코드는
 * **사업장 계정만** 싣는다 — 매장 계정의 대상은 서버가 로그인 매장으로 정한다(I1).
 *
 * @returns {Array<[string, string]>}
 */
export function uploadPartsOf(target) {
  const { storeCd, viewer } = target || {}
  return [[IMPORT_UPLOAD_PART.ATTESTED, 'true']].concat(
    viewer === IMPORT_VIEWER.AGENCY && storeCd != null && storeCd !== ''
      ? [[IMPORT_UPLOAD_PART.STORE_CD, String(storeCd)]]
      : []
  )
}

/** 올리기 전에 막는 크기 — 20MB 를 넘으면 문장, 아니면 `null`. 50MB 를 넘는 요청은 앞단이 사유 없이 끊으므로 화면이 먼저 본다 */
export function fileRejectionOf(file) {
  const size = file && file.size
  return typeof size === 'number' && size > IMPORT_FILE_MAX_BYTES ? REJECTION_TEXT.FILE_TOO_LARGE : null
}

// ─── 거절 (§1-3 f) ───────────────────────────────────────────────────────────────────────────────────

/** 거절 뒤 화면이 할 일 */
export const REJECTION_ACTION = Object.freeze({
  /** 그 자리에 머물고 문장만(파일 고르기 아래 한 줄 등) */
  STAY: 'stay',
  /** 칸 확인 맨 위로 */
  MAPPING: 'mapping',
  /** 문장 없이 다시 읽고 폴링 */
  POLL: 'poll',
  /** 작업을 다시 읽는다(문장과 함께) */
  RELOAD: 'reload',
  /** 다시 읽고 남은 일의 자리로 */
  BLOCK: 'block',
  /** 바닥 줄 */
  FOOT: 'foot',
  /** 첫 화면으로(작업을 닫는다) + 기록 다시 읽기 */
  PICK: 'pick',
  /** 그 자리에 머물고 가져온 기록 다시 읽기 */
  HISTORY: 'history',
})

/** 요청의 종류 — 404 의 뜻이 갈린다(올리기 = 매장, 그 밖 = 작업) */
export const REQUEST_KIND = Object.freeze({ UPLOAD: 'upload', JOB: 'job' })

const reasonAction = Object.freeze({
  NO_TEL_COLUMN: REJECTION_ACTION.MAPPING,
  NOT_READY: REJECTION_ACTION.POLL,
  IMPORTING: REJECTION_ACTION.POLL,
  CLOSED: REJECTION_ACTION.RELOAD,
  PLAN_CHANGED: REJECTION_ACTION.RELOAD,
  ADD_NOT_ALLOWED: REJECTION_ACTION.RELOAD,
  SAME_FILE_NO_ADD: REJECTION_ACTION.RELOAD,
  NO_PEOPLE: REJECTION_ACTION.BLOCK,
  POINT_QUESTIONS: REJECTION_ACTION.BLOCK,
  POINT_CONFLICTS: REJECTION_ACTION.BLOCK,
  STORE_BUSY: REJECTION_ACTION.FOOT,
})

/** 갈래가 있는 거절의 문장 — 세부가 고칠 방법을 바꾼다(I3) */
function variantTextOf(reason, body) {
  if (reason === 'UNSUPPORTED_FORMAT' && body.hint === SPREADSHEET_XML_HINT)
    return REJECTION_VARIANT_TEXT.SPREADSHEET_XML
  if (reason === 'FILE_TOO_LARGE' && OLD_FORMATS.indexOf(body.format) > -1)
    return REJECTION_VARIANT_TEXT.OLD_FORMAT_TOO_LARGE
  if (
    reason === 'INVALID_REQUEST' &&
    Object.prototype.hasOwnProperty.call(rejectionFieldsOf(body), IMPORT_UPLOAD_PART.STORE_CD)
  ) {
    return REJECTION_VARIANT_TEXT.STORE_REQUIRED
  }
  return null
}

/**
 * **거절 → 점주에게 할 말과 화면이 할 일 — 한 곳.** 문장은 `reason` 으로 고른다(서버 `message` 는 기록용 — I1). 남은 일
 * (`NO_PEOPLE`·`POINT_*`)은 [blockNoticeOf] 와 같은 문장이다(그사이 바뀐 경우). 404 는 요청 종류로 갈린다. 전송 실패는 올리기만
 * 따로 말한다 — 300초(로드밸런서 한도)를 넘겨 끊겼을 수 있고 서버는 끝냈을 수 있어 「가져온 기록」으로 안내한다.
 *
 * 화면(경계)은 실패를 `httpStatusOf`·`httpErrorBody`·`isTransportError` 로 읽어 값으로 넘긴다.
 *
 * @param {{status: ?number, body: ?object, transport: boolean, kind: string}} failure
 * @returns {{text: ?string, action: string, block: ?object}}
 */
export function rejectionOf(failure) {
  const { status, body, transport, kind } = failure || {}

  if (transport) {
    return kind === REQUEST_KIND.UPLOAD
      ? { text: REJECTION_VARIANT_TEXT.UPLOAD_TRANSPORT, action: REJECTION_ACTION.HISTORY, block: null }
      : { text: NETWORK_ERROR_MESSAGE, action: REJECTION_ACTION.STAY, block: null }
  }

  if (status === 404) {
    return kind === REQUEST_KIND.UPLOAD
      ? { text: REJECTION_VARIANT_TEXT.STORE_NOT_FOUND, action: REJECTION_ACTION.STAY, block: null }
      : { text: REJECTION_VARIANT_TEXT.JOB_NOT_FOUND, action: REJECTION_ACTION.PICK, block: null }
  }

  const detail = body || {}
  const reason = detail.reason
  if (!reason || !Object.prototype.hasOwnProperty.call(REJECTION_TEXT, reason)) {
    return { text: REJECTION_VARIANT_TEXT.UNKNOWN, action: REJECTION_ACTION.STAY, block: null }
  }

  const action = reasonAction[reason] || REJECTION_ACTION.STAY
  if (action === REJECTION_ACTION.BLOCK) {
    const notice = blockNoticeOf(detail.block || { reason })
    return { text: notice.text, action, block: notice }
  }

  return { text: variantTextOf(reason, detail) || REJECTION_TEXT[reason], action, block: null }
}
